"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useMemo } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cx } from "@/lib/tailwind-utils";
import { EmptyServicesState } from "./empty-services-state";
import { SimpleServiceGroup } from "./helpers";
import { PromotionCategoryBadge, getPromotionCategoryAccordionHeaderClassName, getPromotionCategoryAccordionItemClassName, hasPromotionServices, isPromotionCategoryName } from "./promotion-category";

// At/under this many total services, categories render as always-open
// non-collapsible sections — collapsing only matters when the menu is long
// enough that scanning headers helps.
export const COLLAPSIBLE_CATEGORIES_THRESHOLD = 50;
export interface ServiceAccordionGroup {
  id: string;
}
export interface ServiceAccordionProps<G extends ServiceAccordionGroup> {
  groups: G[];
  renderGroupBody: (group: G, groupIndex: number) => ReactNode;
  renderGroupHeader?: (group: G, groupIndex: number) => ReactNode;
  getServiceCount?: (group: G) => number;
  getItemClassName?: (group: G) => string | undefined;
  getHeaderClassName?: (group: G) => string | undefined;
  emptyState?: ReactNode;
}
const DefaultGroupHeader = t0 => {
  const $ = _c(7);
  const {
    title,
    serviceCount
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-h4 text-primary-foreground text-left">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `${serviceCount} ${serviceCount === 1 ? "service" : "services"}`;
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="text-body text-primary-foreground/70">{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = <div className="flex items-center justify-between w-full"><div className="flex items-center gap-2">{t1}{t3}</div></div>;
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
};
function resolveServiceCount<G extends ServiceAccordionGroup>(group: G, getServiceCount?: (group: G) => number): number {
  if (getServiceCount) return getServiceCount(group);
  const withServices = group as G & {
    services?: {
      length: number;
    };
  };
  return withServices.services?.length ?? 0;
}
function resolveGroupTitle<G extends ServiceAccordionGroup>(group: G): string {
  const withTitle = group as G & {
    title?: string;
  };
  return withTitle.title ?? group.id;
}
export const ServiceAccordion = t0 => {
  const $ = _c(36);
  const {
    groups,
    renderGroupBody,
    renderGroupHeader,
    getServiceCount,
    getItemClassName,
    getHeaderClassName,
    emptyState
  } = t0;
  const totalServices = groups.reduce((n, group) => n + resolveServiceCount(group, getServiceCount), 0);
  const isCollapsible = totalServices > COLLAPSIBLE_CATEGORIES_THRESHOLD;
  let t1;
  if ($[0] !== groups) {
    t1 = groups.map(_temp);
    $[0] = groups;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const defaultOpenValues = t1;
  const isEmpty = groups.length === 0 || groups.every(group_0 => resolveServiceCount(group_0, getServiceCount) === 0);
  if (isEmpty) {
    let t2;
    if ($[2] !== emptyState) {
      t2 = emptyState ?? <EmptyServicesState />;
      $[2] = emptyState;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t2) {
      t3 = <>{t2}</>;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  let t2;
  if ($[6] !== getServiceCount || $[7] !== renderGroupHeader) {
    t2 = (group_1, groupIndex) => {
      if (renderGroupHeader) {
        return renderGroupHeader(group_1, groupIndex);
      }
      return <DefaultGroupHeader title={resolveGroupTitle(group_1)} serviceCount={resolveServiceCount(group_1, getServiceCount)} />;
    };
    $[6] = getServiceCount;
    $[7] = renderGroupHeader;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const renderHeader = t2;
  if (!isCollapsible) {
    let t3;
    if ($[9] !== getHeaderClassName || $[10] !== getItemClassName || $[11] !== groups || $[12] !== renderGroupBody || $[13] !== renderHeader) {
      let t4;
      if ($[15] !== getHeaderClassName || $[16] !== getItemClassName || $[17] !== renderGroupBody || $[18] !== renderHeader) {
        t4 = (group_2, groupIndex_0) => <section key={group_2.id} className={cx("border bg-background rounded-xl border-border/60 shadow-sm", getItemClassName?.(group_2))}><div className={cx("px-4 py-4 bg-primary text-primary-foreground rounded-t-xl", getHeaderClassName?.(group_2))}>{renderHeader(group_2, groupIndex_0)}</div><div className="border-t border-border/40">{renderGroupBody(group_2, groupIndex_0)}</div></section>;
        $[15] = getHeaderClassName;
        $[16] = getItemClassName;
        $[17] = renderGroupBody;
        $[18] = renderHeader;
        $[19] = t4;
      } else {
        t4 = $[19];
      }
      t3 = groups.map(t4);
      $[9] = getHeaderClassName;
      $[10] = getItemClassName;
      $[11] = groups;
      $[12] = renderGroupBody;
      $[13] = renderHeader;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    let t4;
    if ($[20] !== t3) {
      t4 = <div className="space-y-3">{t3}</div>;
      $[20] = t3;
      $[21] = t4;
    } else {
      t4 = $[21];
    }
    return t4;
  }
  let t3;
  if ($[22] !== getHeaderClassName || $[23] !== getItemClassName || $[24] !== groups || $[25] !== renderGroupBody || $[26] !== renderHeader) {
    let t4;
    if ($[28] !== getHeaderClassName || $[29] !== getItemClassName || $[30] !== renderGroupBody || $[31] !== renderHeader) {
      t4 = (group_3, groupIndex_1) => <AccordionItem key={group_3.id} value={group_3.id} className={cx("border bg-background rounded-xl border-border/60 shadow-sm", getItemClassName?.(group_3))}><AccordionTrigger className={cx("px-4 py-3 hover:no-underline hover:md:bg-primary/90 transition-colors bg-primary rounded-t-xl *:text-primary-foreground/80", getHeaderClassName?.(group_3))}>{renderHeader(group_3, groupIndex_1)}</AccordionTrigger><AccordionContent className="pb-0"><div className="border-t border-border/40">{renderGroupBody(group_3, groupIndex_1)}</div></AccordionContent></AccordionItem>;
      $[28] = getHeaderClassName;
      $[29] = getItemClassName;
      $[30] = renderGroupBody;
      $[31] = renderHeader;
      $[32] = t4;
    } else {
      t4 = $[32];
    }
    t3 = groups.map(t4);
    $[22] = getHeaderClassName;
    $[23] = getItemClassName;
    $[24] = groups;
    $[25] = renderGroupBody;
    $[26] = renderHeader;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  let t4;
  if ($[33] !== defaultOpenValues || $[34] !== t3) {
    t4 = <Accordion type="multiple" defaultValue={defaultOpenValues} className="space-y-3">{t3}</Accordion>;
    $[33] = defaultOpenValues;
    $[34] = t3;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  return t4;
};

/** Menu/category groups — same accordion chrome, list body via renderService. */
export const CategoryServiceAccordion = props => {
  const $ = _c(16);
  let renderService;
  let rest;
  if ($[0] !== props) {
    ({
      renderService,
      ...rest
    } = props);
    $[0] = props;
    $[1] = renderService;
    $[2] = rest;
  } else {
    renderService = $[1];
    rest = $[2];
  }
  let t0;
  let t1;
  if ($[3] !== rest) {
    t0 = group => cx(getPromotionCategoryAccordionItemClassName(group.id), rest.getItemClassName?.(group));
    t1 = group_0 => cx(getPromotionCategoryAccordionHeaderClassName(group_0.id), rest.getHeaderClassName?.(group_0));
    $[3] = rest;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  let t2;
  if ($[6] !== rest.renderGroupHeader) {
    t2 = rest.renderGroupHeader ?? _temp2;
    $[6] = rest.renderGroupHeader;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== renderService) {
    t3 = group_2 => <div className="group/list">{group_2.services.map((service, index) => renderService(service, index, group_2.id, group_2.services.length === 1))}</div>;
    $[8] = renderService;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== rest || $[11] !== t0 || $[12] !== t1 || $[13] !== t2 || $[14] !== t3) {
    t4 = <ServiceAccordion {...rest} getItemClassName={t0} getHeaderClassName={t1} renderGroupHeader={t2} renderGroupBody={t3} />;
    $[10] = rest;
    $[11] = t0;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export default ServiceAccordion;
function _temp(g) {
  return g.id;
}
function _temp2(group_1) {
  const isPromotionCategory = isPromotionCategoryName(group_1.id);
  const groupHasPromotionServices = hasPromotionServices(group_1.services);
  const depth = group_1.depth ?? 0;
  return <div className="flex items-center justify-between w-full" style={{
    paddingLeft: `${depth * 20}px`
  }}><div className="flex items-center gap-2">{depth > 0 && <span className="text-foreground-secondary" aria-hidden="true">└</span>}<h3 className={cx("text-left", depth === 0 ? "text-h4" : "text-h5", isPromotionCategory ? "text-foreground" : "text-primary-foreground")}>{group_1.title}</h3>{group_1.parentCategoryName && depth > 0 && <span className="text-caption text-foreground-secondary">in {group_1.parentCategoryName}</span>}{groupHasPromotionServices && <PromotionCategoryBadge />}<span className={cx("text-body", isPromotionCategory ? "text-foreground-secondary" : "text-primary-foreground/70")}>{`${group_1.services.length} ${group_1.services.length === 1 ? "service" : "services"}`}</span></div></div>;
}